import React, { useState, useEffect } from "react";
import image from "../../Assets/images/spinner.gif";
import Loader from "react-loader-spinner";

const Loading = ({ show, currentStep = "Initializing..." }) => {
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [timeLeft, setTimeLeft] = useState(20);
  const [connectionSpeed, setConnectionSpeed] = useState(null);
  const [showSlowConnectionWarning, setShowSlowConnectionWarning] = useState(false);
  const [performanceSource, setPerformanceSource] = useState(null); // 'measured' or 'fetch-failed'

  useEffect(() => {
    if (show && !timeoutReached) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setTimeoutReached(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [show, timeoutReached]);

  useEffect(() => {
    if (show) {
      setTimeLeft(20);
      setTimeoutReached(false);
      detectConnectionSpeed();
    }
  }, [show]);

  // Connection speed detection with actual performance measurement
  const detectConnectionSpeed = () => {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      
      // Measure actual connection performance regardless of type
      measureActualConnectionSpeed();
      
      if (connection.effectiveType) {
        const speed = connection.effectiveType;
        
        // Only store and show warnings for slow connections
        if (speed === 'slow-2g' || speed === '2g' || speed === '3g') {
          setConnectionSpeed(speed);
          setShowSlowConnectionWarning(true);
        } else {
          // Fast connections (4g, 5g) - don't show anything
          setConnectionSpeed(null);
          setShowSlowConnectionWarning(false);
        }
        
        console.log(`🌐 Connection type detected: ${speed}`);
        
        // Monitor connection changes in real-time
        connection.addEventListener('change', () => {
          const newSpeed = connection.effectiveType;
          
          // Only store and show warnings for slow connections
          if (newSpeed === 'slow-2g' || newSpeed === '2g' || newSpeed === '3g') {
            setConnectionSpeed(newSpeed);
            setShowSlowConnectionWarning(true);
          } else {
            // Fast connections - don't show anything
            setConnectionSpeed(null);
            setShowSlowConnectionWarning(false);
          }
          
          console.log(`🌐 Connection type changed to: ${newSpeed}`);
        });
        
      } else if (connection.downlink) {
        // Fallback for browsers without effectiveType
        const downlink = connection.downlink; // Mbps
        if (downlink < 1) {
          // Only store and show warnings for slow connections
          setConnectionSpeed('slow');
          setShowSlowConnectionWarning(true);
        } else {
          // Fast connections - don't show anything
          setConnectionSpeed(null);
          setShowSlowConnectionWarning(false);
        }
        console.log(`🌐 Connection speed: ${downlink} Mbps`);
        
        // Monitor downlink changes
        connection.addEventListener('change', () => {
          const newDownlink = connection.downlink;
          if (newDownlink < 1) {
            // Only store and show warnings for slow connections
            setConnectionSpeed('slow');
            setShowSlowConnectionWarning(true);
          } else {
            // Fast connections - don't show anything
            setConnectionSpeed(null);
            setShowSlowConnectionWarning(false);
          }
          console.log(`🌐 Connection speed changed to: ${newDownlink} Mbps`);
        });
      }
    } else {
      // Fallback for browsers without Network Information API
      console.log("🌐 Network Information API not available");
      measureActualConnectionSpeed();
    }
  };

  // Measure actual connection performance regardless of connection type
  const measureActualConnectionSpeed = async () => {
    try {
      const startTime = performance.now();
      
      // Test with a small image or API call to measure actual performance
      // This endpoint (https://httpbin.org/delay/0.1) has a 100ms delay built-in
      // We add a 5-second timeout - if it fails or times out, we treat it as slow performance
      const testUrl = 'https://httpbin.org/delay/0.1'; // 100ms delay
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(testUrl, { 
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache' // Prevent caching
      });
      
      clearTimeout(timeoutId);
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      console.log(`🌐 Actual response time: ${responseTime.toFixed(0)}ms`);
      
      // Determine actual performance based on response time
      if (responseTime < 200) {
        // Fast performance - don't show any warnings or status
        setShowSlowConnectionWarning(false);
        setConnectionSpeed(null); // Don't store fast performance
        setPerformanceSource(null);
        console.log(`🌐 Fast performance: ${responseTime.toFixed(0)}ms - No warnings needed`);
      } else if (responseTime < 1000) {
        // Moderate performance - don't show warnings
        setShowSlowConnectionWarning(false);
        setConnectionSpeed(null); // Don't store moderate performance
        setPerformanceSource(null);
        console.log(`🌐 Moderate performance: ${responseTime.toFixed(0)}ms - No warnings needed`);
      } else {
        // Slow performance - show warnings and status
        setShowSlowConnectionWarning(true);
        setConnectionSpeed('slow');
        setPerformanceSource('measured');
        console.log(`🌐 Slow performance: ${responseTime.toFixed(0)}ms - Warnings shown`);
      }
      
    } catch (error) {
      console.warn("🌐 Performance test failed:", error);
      
      // If performance test fails (including timeout), treat as slow performance
      // This ensures users get connection feedback even when the test fails
      setShowSlowConnectionWarning(true);
      setConnectionSpeed('slow');
      setPerformanceSource('fetch-failed');
      
      // Log the specific failure reason
      if (error.name === 'AbortError') {
        console.log("🌐 Performance test timed out after 5 seconds - treating as slow performance");
      } else {
        console.log("🌐 Performance test failed with error - treating as slow performance");
      }
    }
  };

  if (!show) return null;

  return (
    <div className="loading-overlay">
      <div className="loading-content" style={{ 
        position: "relative", 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px"
      }}>
        <img src={image} style={{ maxWidth: "85px" }} alt="Loading..." />
        
        {/* Current Step Indicator - Stuck to absolute bottom of page */}
        <div className="loading-step" style={{ 
          fontSize: "11px", 
          color: "#888", 
          textAlign: "center",
          maxWidth: "300px",
          position: "fixed",
          bottom: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          zIndex: 9999,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          padding: "5px 10px",
          borderRadius: "4px"
        }}>
          {currentStep}
          {/* Only show connection status for slow performance */}
          {connectionSpeed && (connectionSpeed === 'slow' || connectionSpeed === 'slow-2g' || connectionSpeed === '2g' || connectionSpeed === '3g') && (
            <div style={{ 
              fontSize: "9px", 
              color: "#ff8c00", 
              marginTop: "3px",
              opacity: 0.9
            }}>
              {performanceSource === 'fetch-failed' ? (
                <>📶 Slow performance detected-0.1</>
              ) : (
                <>📶 Slow performance detected</>
              )}
            </div>
          )}
        </div>
        
        {/* Slow Connection Warning */}
        {/* {showSlowConnectionWarning && (
          <div className="slow-connection-warning" style={{
            fontSize: "10px",
            color: "#ff8c00",
            marginTop: "8px",
            textAlign: "center",
            padding: "6px 12px",
            backgroundColor: "rgba(255, 140, 0, 0.1)",
            borderRadius: "4px",
            border: "1px solid rgba(255, 140, 0, 0.3)"
          }}>
            🌐 Slow connection detected - Loading may take longer than expected
          </div>
        )} */}
        
        {/* Timeout Warning - Simplified */}
        {timeLeft <= 5 && !timeoutReached && (
          <div className="timeout-warning" style={{
            fontSize: "10px",
            color: "#ff6b6b",
            marginTop: "10px",
            textAlign: "center"
          }}>
            ⚠️ Loading taking longer than expected... ({timeLeft}s)
          </div>
        )}
      </div>
    </div>
  );
};

export default Loading;
